import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-duotone-svg-icons';
import { advantages } from '@/data/advantageData';
import { SubscriptionTier } from '@/actions/dtos/subscription';
import { BillingPeriod } from '@/types/billingPeriod';
import { usePricing } from '@/hooks/usePricing';

interface Props {
  tier: SubscriptionTier;
  billingPeriod: BillingPeriod;
  discountCode?: string;
  renderCardButton?: () => React.ReactNode;
}

const getBgColor = (idx: number, reverse: boolean) =>
  reverse ? (idx % 2 === 0 ? 'bg-base-200' : 'bg-base-100') : idx % 2 === 0 ? 'bg-base-100' : 'bg-base-200';

const SubscriptionComparisonCard: React.FC<Props> = ({ tier, billingPeriod, discountCode, renderCardButton }) => {
  const { details, isLoading } = usePricing(tier, billingPeriod, discountCode);
  if (isLoading || !details) return null;

  const { formatted, periodDiscount, couponDiscount, couponDiscountRelative } = details;
  const hasBasicDiscount = periodDiscount > 0;
  const hasReferralDiscount = couponDiscount > 0;

  return (
    <div className="flex w-full flex-col items-center rounded-lg border border-primary bg-base-100 py-4 text-center md:w-5/6 lg:w-full">
      <div className="flex h-14 w-full items-center justify-center font-bold sm:w-3xs lg:w-2xs">{tier}</div>

      <div className="w-full bg-base-200">
        <div className={`flex h-14 items-center justify-center ${hasBasicDiscount ? 'text-xl text-accent' : ''}`}>
          <span>{formatted.periodPrice}</span>
          {hasBasicDiscount && <span className="ml-2 text-base text-primary line-through">{formatted.basePrice}</span>}
        </div>
      </div>

      {discountCode && (
        <div className="flex h-14 w-full items-center justify-center text-xl">
          {hasReferralDiscount && (
            <div>
              <span className="text-accent">{formatted.totalPrice}</span>
              <span className="ml-2 text-base text-primary">(-{couponDiscountRelative}%)</span>
            </div>
          )}
        </div>
      )}

      {advantages.map((adv, index) => {
        const cell = adv.data.get(tier);
        return (
          <div key={index} className={`w-full ${getBgColor(index, !!discountCode)}`}>
            <div className="flex h-14 items-center justify-center">
              {cell === '✓' ? (
                <FontAwesomeIcon icon={faCheck} className="h-4 w-4" />
              ) : (
                <span className="line-clamp-1">{cell}</span>
              )}
            </div>
          </div>
        );
      })}

      {renderCardButton && renderCardButton()}
    </div>
  );
};

export default SubscriptionComparisonCard;
